import { Section } from './Section';
import * as THREE from 'three';
import Resources from '../Utils/Resources';
import heroVideoVertexShader from '../shaders/heroVideo/vertex.glsl';
import heroVideoFragmentShader from '../shaders/heroVideo/fragment.glsl';
import { Sizes } from '../Core/Sizes';
import Time from '../Core/Time';
import gsap from 'gsap';
import { SplitText } from "../../js/vendors/gsap/SplitText";

gsap.registerPlugin(SplitText);

/**
 * First section implementation
 * Displays the current background with circle objects
 */
export class SectionHero extends Section {
  /**
   * Initialize section elements
   * Override base method
   */
  initialize() {
    // Call parent initialize to create background
    super.initialize();

    this.resources = Resources.getInstance();
    this.time = Time.getInstance();
    this.sizes = Sizes.getInstance();

    this.prositionOffset = 0.001;

    this.videoSizes = [
      {
        width: 1920,
        height: 1080
      }
    ];

    // Initialize video quad with placeholder texture
    this.initVideoQuad();

    // Initialize animations but don't start them
    this.initAnimations();

    // Set up resource loading listener
    this.resources.on('resource', (resourceName) => {
      if(resourceName === 'heroVideo'){
        this.initVideoTexture();
        this.startAnimations();
      }
    });

    // Add visibility change listener
    document.addEventListener('visibilitychange', this.onPageVisibilityChange.bind(this));

    this.on('visibilityChanged', this.onVisibilityChange.bind(this));

    this.sizes.on('resize', this.onResize.bind(this));
  }

  initVideoQuad(){
    this.geometry = new THREE.PlaneGeometry(1, 1);
    this.material = new THREE.ShaderMaterial({
      vertexShader: heroVideoVertexShader,
      fragmentShader: heroVideoFragmentShader,
      uniforms: {
        uVideoTexture: { value: null }, // Will be updated when video is ready
        uResolution: { value: new THREE.Vector4(1, 1, 1, 1) },
        uOverlayOpacity: { value: 0.5 },
        uOverlayOpacityEnd: { value: 0.9 },
        uVideoAspectRatio: { value: 16/9 } // Default aspect ratio
      },
      transparent: false,
      side: THREE.FrontSide
    });

    this.videoQuad = new THREE.Mesh(
      this.geometry,
      this.material
    );

    this.videoQuad.scale.set(
      this.sectionData.worldData.width,
      this.sectionData.worldData.height,
      1
    );

    this.videoQuad.position.z = this.prositionOffset;
    this.videoQuad.name = `${this.constructor.name}-VideoQuad`;
    this.group.add(this.videoQuad);
  }

  initVideoTexture(){
    if (!this.resources.items.heroVideo) return;

    this.videoElement = this.resources.items.heroVideo.videoElement;
    this.videoTexture = this.resources.items.heroVideo.videoTexture;

    this.videoTexture.colorSpace = THREE.SRGBColorSpace;
    this.videoTexture.minFilter = THREE.LinearFilter;
    this.videoTexture.magFilter = THREE.LinearFilter;
    this.videoTexture.generateMipmaps = false;
    this.videoTexture.needsUpdate = true;

    // Update material with video texture
    this.material.uniforms.uVideoTexture.value = this.videoTexture;

    // Calculate and update video aspect ratio
    const videoAspect = this.videoElement.videoWidth / this.videoElement.videoHeight;
    this.material.uniforms.uVideoAspectRatio.value = videoAspect;

    // Update resolution
    const resolution = this.calculateVideoTextureAspectRatio();
    this.material.uniforms.uResolution.value = new THREE.Vector4(resolution.x, resolution.y, resolution.z, resolution.w);

    // Start video playback
    this.videoElement.play();
  }

  initAnimations() {
    // Get DOM elements
    const logo = document.querySelector('.gt-banner .logo');
    const logos = document.querySelectorAll('.gt-banner .footer ul li');
    const partnership = document.querySelector('.gt-banner .partnership');
    const svgElements = document.querySelectorAll('.game-logo-svg-container > *');

    // Split h1 text into words
    this.heroTitle = {
      h1: document.querySelector('.gt-banner h1'),
    };

    this.heroTitle.h1Split = new SplitText(this.heroTitle.h1, { type: "words" });
    this.heroTitle.h1Words = this.heroTitle.h1Split.words;

    this.heroTitle.h1Words.forEach((word, i) => {
      word.classList.add('js-word');
      return word.innerHTML = '<div class="js-inner">' + word.innerHTML + '</div>'
    });

    this.heroTitle.h1WordsInner = [...this.heroTitle.h1.querySelectorAll('.js-inner')];

    // Set initial states
    gsap.set(svgElements, {
      opacity: 0,
      scale: 0.95,
      svgOrigin: "50% 50%"
    });

    gsap.set(this.heroTitle.h1WordsInner, {
      opacity: 0,
      y: 20
    });

    gsap.set(logo, {
      opacity: 1,
      visibility: 'visible',
    });

    gsap.set(this.heroTitle.h1, {
      opacity: 1,
      visibility: 'visible',
    });

    // Create timeline but don't play it yet
    this.animationTimeline = gsap.timeline({
      delay: 0.1,
      paused: true // Start paused
    });

    this.animationTimeline
      .to(svgElements, {
        opacity: 1,
        scale: 1,
        svgOrigin: "50% 50%",
        duration: 1,
        ease: "back.out(1.7)",
        stagger: {
          each: 0.05,
          from: "center"
        }
      })
      .to(this.heroTitle.h1WordsInner, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power2.out",
        stagger: {
          each: 0.1
        }
      }, '<.5')
      .fromTo(logos, {
        y: 40,
        opacity: 0,
        visibility: 'hidden',
      },{
        y: 0,
        opacity: 1,
        visibility: 'visible',
        duration: 0.8,
        ease: 'power2.out',
        stagger: {
          each: 0.1
        }
      }, '<0.3')
      .fromTo(partnership, {
        y: 40,
        opacity: 0,
        visibility: 'hidden',
      },{
        y: 0,
        opacity: 1,
        visibility: 'visible',
        duration: 0.8,
        ease: 'power2.out'
      }, '<0.2');
  }

  startAnimations() {
    if (this.animationTimeline) {
      this.animationTimeline.play();
    }
  }

  resize(){
    super.resize();

    this.videoQuad.scale.set(
      this.sectionData.worldData.width,
      this.sectionData.worldData.height,
      1
    );

    const resolution = this.calculateVideoTextureAspectRatio();
    this.material.uniforms.uResolution.value = new THREE.Vector4(resolution.x, resolution.y, resolution.z, resolution.w);

  }

  calculateVideoTextureAspectRatio(){
    const sizeIndex = 0;
    const videoSize = this.videoSizes[sizeIndex];
    const videoWidth = videoSize.width;
    const videoHeight = videoSize.height;

    const videoAspect = videoHeight / videoWidth;

    let a1;
    let a2;
    if (this.sectionData.worldData.height / this.sectionData.worldData.width > videoAspect) {
        a1 = (this.sectionData.worldData.width / this.sectionData.worldData.height) * videoAspect;
        a2 = 1;
    } else {
        a1 = 1;
        a2 = this.sectionData.worldData.height / this.sectionData.worldData.width / videoAspect;
    }

    const x = this.sectionData.worldData.width;
    const y = this.sectionData.worldData.height;
    const z = a1;
    const w = a2;

    return { x, y, z, w };
  }

  onVisibilityChange(visible) {
    if(this.videoElement){
      if(visible){
        this.videoElement.play();
      } else {
        this.videoElement.pause();
      }
    }
  }

  onPageVisibilityChange() {
    if (this.videoElement) {
      if (document.hidden) {
        this.videoElement.pause();
      } else {
        // Try to play the video
        const playPromise = this.videoElement.play();

        // Handle play promise rejection
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.log('Video play failed:', error);
            // Try again after a short delay
            setTimeout(() => {
              this.videoElement.play().catch(e => console.log('Second play attempt failed:', e));
            }, 100);
          });
        }
      }
    }
  }

  onResize() {
    // Check if orientation changed
    const isPortrait = this.sizes.isPortrait;
    const currentVideo = this.resources.items.heroVideo;

    if (currentVideo && currentVideo.videoElement) {
      const currentOrientation = currentVideo.videoElement.dataset.orientation;
      const newOrientation = isPortrait ? 'portrait' : 'landscape';

      if (currentOrientation !== newOrientation) {
        // Reload video for new orientation
        this.resources.reloadVideoForOrientation('heroVideo', isPortrait);
      }
    }
  }
}
